/**
 * Low-level numeric + color helpers shared across the 2D SVG Diagram modules
 * (geometry, render, heat math). Kept dependency-free (only `Vec2`) so every
 * other module can import from here without cycles.
 */

import type { Vec2 } from "./types";

/** Round to 2 decimals — used to keep emitted SVG path data compact. */
export function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

export function roundVec2(v: Vec2): Vec2 {
    return { x: round2(v.x), y: round2(v.y) };
}

/** Parse a `#rgb`/`#rrggbb` hex string to [r,g,b] (0..255), or null if invalid. */
export function parseHexColor(hex: string): [number, number, number] | null {
    const s = hex.trim().replace(/^#/, "");
    if (s.length === 3) {
        const r = parseInt(s[0]! + s[0]!, 16);
        const g = parseInt(s[1]! + s[1]!, 16);
        const b = parseInt(s[2]! + s[2]!, 16);
        return [r, g, b];
    }
    if (s.length === 6) {
        const r = parseInt(s.slice(0, 2), 16);
        const g = parseInt(s.slice(2, 4), 16);
        const b = parseInt(s.slice(4, 6), 16);
        if ([r, g, b].some((n) => Number.isNaN(n))) return null;
        return [r, g, b];
    }
    return null;
}

export function toHex2(n: number): string {
    return Math.max(0, Math.min(255, Math.round(n)))
        .toString(16)
        .padStart(2, "0");
}

/** Linearly blend two hex colors by `t` (0 = `a`, 1 = `b`). */
export function lerpHexColor(a: string, b: string, t: number): string {
    const ca = parseHexColor(a);
    const cb = parseHexColor(b);
    if (!ca || !cb) return t >= 0.5 ? b : a;
    const k = Math.max(0, Math.min(1, t));
    const r = ca[0] + (cb[0] - ca[0]) * k;
    const g = ca[1] + (cb[1] - ca[1]) * k;
    const bl = ca[2] + (cb[2] - ca[2]) * k;
    return `#${toHex2(r)}${toHex2(g)}${toHex2(bl)}`;
}

/** Shortest distance from `p` to an axis-aligned rectangle (0 when inside). */
export function distanceToRect(p: Vec2, min: Vec2, max: Vec2): number {
    const dx = Math.max(min.x - p.x, 0, p.x - max.x);
    const dy = Math.max(min.y - p.y, 0, p.y - max.y);
    return Math.hypot(dx, dy);
}

/** Deterministic 32-bit string hash (seeds stable "random" object ordering). */
export function hashString(str: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
